/**
 * Booking eligibility helpers.
 *
 * Extracted so the logic can be unit-tested in isolation without dragging in
 * the full GetBookingToRightDataAction dependency graph.
 */

import { AdmissionStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

type AdmissionLike = { status?: string | null } | null | undefined;

type TripCoverageLike = { coveredTripId?: string | null } | null | undefined;

type BookedOfferLike = {
    admissions?: AdmissionLike[] | null;
    tripCoverage?: TripCoverageLike;
} | null | undefined;

type TripLike = {
    id?: string | null;
    direction?: string | null;
} | null | undefined;

// REFUNDED / RELEASED are end-states that don't represent a pending or used-up
// modification slot. EXCHANGED is NOT historical here: per backend rules, an
// EXCHANGED admission on a direction consumes that direction's single
// exchange-slot, so it must block further exchanges on that direction.
const HISTORICAL_ADMISSION_STATUSES: Set<string> = new Set([
    AdmissionStatusEnum.REFUNDED,
    AdmissionStatusEnum.RELEASED,
]);

/**
 * Decides whether a booking (or one direction of it) still has its single
 * exchange-slot available.
 *
 * Backend rule (confirmed): one exchange per booking, with round-trip as an
 * exception — round-trip bookings get one exchange per direction (outbound
 * and inbound are independent slots). So once a direction has an EXCHANGED
 * admission, that direction is locked; the other direction stays eligible.
 *
 * Returns `true` (the flag name means "no pending modification") when every
 * active admission on the inspected scope is FULFILLED, and `false` as soon
 * as one admission is in any other state — including EXCHANGED, which is the
 * marker that the slot has been used.
 *
 * Scope:
 * - When called with `(bookedOffers)` alone, evaluates the whole booking. Any
 *   EXCHANGED admission anywhere returns false. Use this for one-way
 *   bookings or when you don't have trip-direction context.
 * - When called with `(bookedOffers, trips, direction)`, only considers
 *   offers whose `tripCoverage.coveredTripId` belongs to a trip with the
 *   given `direction`. Use this for round-trip per-leg eligibility.
 *
 * An offer whose admissions are entirely historical (REFUNDED / RELEASED)
 * or empty / missing contributes nothing to the decision.
 */
export function computeNoModificationOnBooking(
    bookedOffers: BookedOfferLike[],
    trips?: TripLike[],
    direction?: string,
): boolean {
    let offersToCheck = bookedOffers;
    if (direction && trips && trips.length > 0) {
        offersToCheck = bookedOffers.filter((offer) => {
            const tripId = offer?.tripCoverage?.coveredTripId;
            if (!tripId) return false;
            const trip = trips.find((t) => t?.id === tripId);
            return trip?.direction === direction;
        });
    }

    for (let i = 0; i < offersToCheck.length; i++) {
        const offer = offersToCheck[i];
        const activeAdmissions = (offer?.admissions || []).filter(
            (a): a is { status: string } =>
                !!a && typeof a.status === "string" && !HISTORICAL_ADMISSION_STATUSES.has(a.status),
        );
        if (activeAdmissions.length === 0) continue;
        const anyNonFulfilled = activeAdmissions.some((a) => a.status !== AdmissionStatusEnum.FULFILLED);
        if (anyNonFulfilled) return false;
    }
    return true;
}
